//=====================================
// jQuery
//=====================================
window.$ = window.jQuery = require('jquery');
global.$ = global.jQuery = require('jquery');
window.App = window.App || {};

//=====================================
// GSAP
//=====================================
import { gsap } from "gsap/dist/gsap";
import { ScrollTrigger } from "gsap/dist/ScrollTrigger";
import { ScrollToPlugin } from "gsap/dist/ScrollToPlugin";

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

ScrollTrigger.config({
  ignoreMobileResize: true
});

//=====================================
// SplitType
//=====================================
const SplitType = require('split-type').default;
window.SplitType = SplitType;

//=====================================
// Utility
//=====================================
const mqSP = window.matchMedia('(max-width: 767px)');
const mqPC = window.matchMedia('(min-width: 768px)');
const mqVerySmall = window.matchMedia('(max-width: 374px)');

function isSP() {
  return mqSP.matches;
}

function isPC() {
  return mqPC.matches;
}

function isVerySmall() {
  return mqVerySmall.matches;
}

function rafThrottle(fn) {
  let ticking = false;

  return function(...args) {
    if (ticking) return;

    ticking = true;

    requestAnimationFrame(() => {
      fn.apply(this, args);
      ticking = false;
    });
  };
}

function debounce(fn, delay = 250) {
  let timer = null;

  return function(...args) {
    clearTimeout(timer);

    timer = setTimeout(() => {
      fn.apply(this, args);
    }, delay);
  };
}

function setAutoScrollBehavior() {
  if ('scrollRestoration' in history) {
    history.scrollRestoration = 'manual';
  }

  document.documentElement.style.scrollBehavior = 'auto';
}

//=====================================
// Loader
//=====================================
function initLoader() {
  window.addEventListener('load', function() {
    const loader = document.querySelector('#vision-loader');
    const minLoadingTime = 1;
    gsap.delayedCall(minLoadingTime, function() {
      if (!loader) {
        initTopIntroAnimation();
        return;
      }
      gsap.to(loader, {
        opacity: 0,
        duration: 0.5,
        ease: "power2.out",
        onComplete: function() {
          loader.style.display = 'none';

          gsap.delayedCall(0.35, function() {
            initTopIntroAnimation();
          });
        }
      });
    });
  });
}

//=====================================
// Split Text
//=====================================
function initSplitText() {
  if (!window.SplitType) {
    console.error('SplitType is not loaded');
    return;
  }

  new window.SplitType('.js-split', {
    types: 'lines, words, chars'
  });
}

//=====================================
// Header Scroll State
//=====================================
function initHeaderScrollState() {
  const header = document.querySelector('#header');
  const headerH = header ? header.offsetHeight : 0;

  function fixedAnime() {
    if (window.scrollY >= headerH) {
      document.body.classList.add('is_scroll');
    } else {
      document.body.classList.remove('is_scroll');
    }
  }

  const fixedAnimeRaf = rafThrottle(fixedAnime);

  window.addEventListener('scroll', fixedAnimeRaf, {
    passive: true
  });

  fixedAnime();

  return fixedAnime;
}


//=====================================
// Smooth Anchor Scroll
// 通常のページ内アンカー用
//=====================================
function initSmoothAnchorScroll() {
  document.querySelectorAll('a[href^="#"]').forEach((link) => {
    // NEXTボタンは既存処理に任せる
    if (link.closest('#fixed-next-btn')) return;

    link.addEventListener('click', function(e) {
      const hash = this.getAttribute('href');

      if (!hash || hash === '#') return;

      const target = document.querySelector(hash);

      if (!target) return;

      e.preventDefault();

      gsap.to(window, {
        scrollTo: {
          y: target,
          autoKill: false
        },
        duration: isSP() ? 0.75 : 1.1,
        ease: "power3.out",
        onComplete: function() {
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      });
    });
  });
}






//=====================================
// Hash Control
// ・初回ロード時はhashを削除してTOPから表示
// ・スクロール後もしばらくしたらhashを削除
//=====================================
function initHashControl() {
  if (window.location.hash) {
    history.replaceState(null, '', window.location.pathname + window.location.search);
    window.scrollTo(0, 0);
  }

  let hashClearTimer = null;

  window.addEventListener('scroll', function() {
    if (!window.location.hash) return;

    clearTimeout(hashClearTimer);

    hashClearTimer = setTimeout(function() {
      if (window.location.hash) {
        history.replaceState(null, '', window.location.pathname + window.location.search);
      }
    }, 450);
  }, {
    passive: true
  });
}

//=====================================
// Horizontal Service Scroll
// PCのみpin。SPはCSSの通常横スクロールに任せる
//=====================================
function initHorizontalServiceScroll() {
  if (!isPC()) return;

  document.querySelectorAll('.horizontal-slide-gsap').forEach((track) => {
    const section = track.closest('#section-service');
    const items = gsap.utils.toArray(track.querySelectorAll('.item-slide'));

    if (!section || items.length <= 1) return;

    gsap.set(track, {
      x: 0,
      force3D: false
    });

    const getDistance = () => {
      return Math.max(0, track.scrollWidth - window.innerWidth);
    };

    gsap.to(track, {
      x: () => -getDistance(),
      ease: 'none',
      scrollTrigger: {
        id: 'service-horizontal',
        trigger: section,
        start: 'top top',
        end: () => '+=' + getDistance(),
        pin: true,
        scrub: 0.8,
        anticipatePin: 1,
        invalidateOnRefresh: true
      }
    });

    gsap.to(section, {
      '--service-bg-y': '-420px',
      ease: 'none',
      scrollTrigger: {
        id: 'service-bg',
        trigger: section,
        start: 'top top',
        end: () => '+=' + getDistance(),
        scrub: 0.8,
        invalidateOnRefresh: true
      }
    });
  });
}



//=====================================
// Horizontal Service Scroll内にいるときの
// NEXTアンカーボタンの判定用
//=====================================
function getActiveSection() {
  const serviceTrigger = ScrollTrigger.getById('service-horizontal');
  if (serviceTrigger && serviceTrigger.isActive) {
    return document.querySelector('#section-service');
  }
  const viewportCenter = window.scrollY + window.innerHeight * 0.5;
  let active = null;

  for (const section of nextSections) {
    const top = section.offsetTop;
    const bottom = top + section.offsetHeight;

    if (viewportCenter >= top && viewportCenter < bottom) {
      active = section;
      break;
    }
  }
  return active;
}







//=====================================
// Title Text Animation
// #section-top は専用処理。
// #section-gallery は軽量処理。
//=====================================
function initTitleAnimations() {
  const sections = gsap.utils.toArray('.m-section:not(#section-top)');//:not(#section-gallery)

  sections.forEach((section) => {
    const chars = section.querySelectorAll('.tit-m .char');
    const titTopChars = section.querySelectorAll('.tit-top .char');

    if (titTopChars.length) {
      gsap.set(titTopChars, {
        x: 0,
        opacity: 0,
        force3D: false
      });

      gsap.to(titTopChars, {
        y: 0,
        opacity: 1,
        ease: "power2.out",
        duration: isSP() ? 0.45 : 0.8,
        stagger: isSP() ? 0.025 : 0.04,
        scrollTrigger: {
          trigger: section,
          start: 'top 80%',
          toggleActions: "play none none none",
          once: true,
          invalidateOnRefresh: false
        }
      });
    }

    if (!chars.length) return;

    const sp = isSP();

    chars.forEach((char) => {
      gsap.set(char, {
        x: sp ? gsap.utils.random(-180, 180) : gsap.utils.random(-900, 900),
        y: sp ? gsap.utils.random(-180, 180) : gsap.utils.random(-900, 900),
        scale: sp ? gsap.utils.random(0.65, 2.0) : gsap.utils.random(0.2, 8),
        opacity: 0,
        rotation: sp ? gsap.utils.random(-60, 60) : gsap.utils.random(-240, 240),
        force3D: false
      });
    });

    gsap.to(chars, {
      x: 0,
      y: 0,
      scale: 1,
      opacity: 1,
      rotation: 0,
      duration: sp ? 0.55 : 0.9,
      ease: "power3.out",
      stagger: {
        amount: sp ? 0.25 : 0.55,
        from: "center"
      },
      scrollTrigger: {
        trigger: section,
        start: 'top 80%',
        toggleActions: "play none none none",
        once: true,
        invalidateOnRefresh: false
      },
      onComplete: function() {
        gsap.set(chars, {
          opacity: 1,
          clearProps: "transform"
        });
      }
    });
  });
}

//=====================================
// Gallery Title / 軽量表示
//=====================================
function initGalleryTitle() {
  const galleryTitleChars = document.querySelectorAll('#section-gallery .tit-m .char');

  if (!galleryTitleChars.length) return;

  gsap.set(galleryTitleChars, {
    y: isSP() ? 16 : 24,
    opacity: 0,
    force3D: false
  });

  gsap.to(galleryTitleChars, {
    y: 0,
    opacity: 1,
    duration: isSP() ? 0.45 : 0.65,
    ease: "power2.out",
    stagger: {
      amount: isSP() ? 0.12 : 0.22,
      from: "center"
    },
    scrollTrigger: {
      trigger: '#section-gallery',
      start: 'top 80%',
      toggleActions: "play none none none",
      once: true,
      invalidateOnRefresh: false
    },
    onComplete: function() {
      gsap.set(galleryTitleChars, {
        opacity: 1,
        clearProps: "transform"
      });
    }
  });
}

//=====================================
// TOP Background Parallax
// 
//=====================================
function initTopParallax() {
  const sectionTop = document.querySelector('#section-top');
  if (!sectionTop) return;
  const picture = sectionTop.querySelector('.this-picture');
  if (!picture) return;
  const sp = isSP();
  gsap.to(picture, {
    y: sp ? -60 : -150,
    scale: sp ? 1.18 : 1.7,
    rotation: sp ? -3 : -12,
    ease: "none",
    force3D: false,
    scrollTrigger: {
      id: 'top-parallax',
      trigger: sectionTop,
      start: 'top top',
      end: 'bottom top',
      scrub: sp ? 0.7 : true,
      invalidateOnRefresh: true
    }
  });
}
//=====================================
// TOP / First View Intro Animation
//=====================================
let topIntroPlayed = false;
function initTopIntroAnimation() {
  if (topIntroPlayed) return;

  const sectionTop = document.querySelector('#section-top');
  if (!sectionTop) return;
  topIntroPlayed = true;
  const house = sectionTop.querySelector('.fig_house');
  const media = sectionTop.querySelector('.this-media');
  const titleChars = sectionTop.querySelectorAll('.tit-m .char');
  const topChars = sectionTop.querySelectorAll('.tit-top .char');

  const pc = isPC();
  const verySmall = isVerySmall();

  gsap.killTweensOf([house, media, titleChars, topChars]);

  sectionTop.classList.add('is-top-ready');

  if (media) {
    gsap.set(media, {
      opacity: 0,
      scale: 1.08,
      y: 0,
      filter: verySmall || isSP() ? "none" : "blur(8px)",
      force3D: false
    });
  }

  if (house) {
    gsap.set(house, {
      y: verySmall ? 120 : 200,
      scale: verySmall ? 0.82 : 0.68,
      opacity: 0,
      rotate: 0.01,
      filter: verySmall || isSP() ? "none" : "blur(18px)",
      transformOrigin: "center bottom",
      force3D: false
    });
  }

  if (topChars.length) {
    gsap.set(topChars, {
      x: verySmall ? -100 : -420,
      opacity: 0,
      force3D: false
    });
  }

  if (titleChars.length) {
    gsap.set(titleChars, {
      x: () => verySmall ? gsap.utils.random(-120, 120) : pc ? gsap.utils.random(-900, 900) : gsap.utils.random(-180, 180),
      y: () => verySmall ? gsap.utils.random(-120, 120) : pc ? gsap.utils.random(-900, 900) : gsap.utils.random(-180, 180),
      scale: () => verySmall ? gsap.utils.random(0.75, 1.65) : pc ? gsap.utils.random(0.25, 8) : gsap.utils.random(0.65, 2.0),
      opacity: 0,
      rotation: () => verySmall ? gsap.utils.random(-40, 40) : pc ? gsap.utils.random(-240, 240) : gsap.utils.random(-60, 60),
      force3D: false
    });
  }

  const tl = gsap.timeline();

  if (media) {
    tl.to(media, {
      opacity: 1,
      scale: 1,
      filter: "blur(0px)",
      duration: verySmall ? 0.55 : 0.85,
      ease: "power3.out"
    }, 0);
  }

  if (titleChars.length) {
    tl.to(titleChars, {
      x: 0,
      y: 0,
      scale: 1,
      opacity: 1,
      rotation: 0,
      duration: verySmall ? 0.5 : pc ? 0.9 : 0.6,
      ease: "power3.out",
      stagger: {
        amount: verySmall ? 0.18 : pc ? 0.55 : 0.28,
        from: "center"
      },
      onComplete: function() {
        gsap.set(titleChars, {
          opacity: 1,
          clearProps: "transform"
        });
      }
    }, 0.2);
  }

  if (house) {
    tl.to(house, {
      y: 0,
      scale: 1,
      opacity: 1,
      filter: "blur(0px)",
      duration: verySmall ? 0.85 : 1.45,
      ease: "power4.out"
    }, ">-0.05");
  }

  if (topChars.length) {
    tl.to(topChars, {
      x: 0,
      opacity: 1,
      duration: 0.22,
      ease: "power3.out",
      stagger: {
        each: verySmall ? 0.035 : 0.065,
        from: "start"
      },
      onComplete: function() {
        gsap.set(topChars, {
          opacity: 1,
          clearProps: "transform"
        });
      }
    }, "<+0.18");
  }

  tl.call(function() {
    gsap.set([media, house], {
      clearProps: "filter"
    });

    // SPでは永続ループを切る。低スペック端末の文字欠け・引っ掛かり対策。
    if (!pc) return;

    if (media) {
      gsap.to(media, {
        scale: 1.035,
        y: -12,
        duration: 8,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        overwrite: false
      });
    }

    if (house) {
      gsap.to(house, {
        y: -24,
        duration: 3.6,
        repeat: -1,
        yoyo: true,
        ease: "sine.inOut",
        overwrite: true
      });
    }
  });
}

//=====================================
// Vision Parallax
// SPも動かすが、移動量は軽くする
//=====================================
function initVisionParallax(selector, visualVars) {
  const sections = document.querySelectorAll(selector);

  sections.forEach((sectionElement) => {
    const visualElement = sectionElement.querySelector('.this-picture');

    if (!visualElement) return;

    const {
      start = 'top top',
      end = 'bottom top',
      scrub = true,
      ease = 'none',
      sp = {},
      ...vars
    } = visualVars;

    const useVars = isSP() ? { ...vars, ...sp } : vars;

    gsap.to(visualElement, {
      ...useVars,
      ease: ease,
      force3D: false,
      scrollTrigger: {
        trigger: sectionElement,
        start: start,
        end: end,
        scrub: scrub,
        invalidateOnRefresh: true
      }
    });
  });
}

function initBackgroundParallax() {
  initTopParallax();

  initVisionParallax('.m-section.gsap-box-b', {
    y: 260,
    scale: 1.12,
    sp: {
      y: 80,
      scale: 1.06
    }
  });

  initVisionParallax('.m-section.gsap-box-c', {
    scale: 1.25,
    opacity: 0.8,
    sp: {
      scale: 1.08,
      opacity: 1
    }
  });

  initVisionParallax('.m-section.gsap-box-d', {
    x: -140,
    scale: 1.16,
    sp: {
      x: -40,
      scale: 1.06
    }
  });

  initVisionParallax('.m-section.gsap-box-f', {
    y: -100,
    scale: 1.06,
    opacity: 0.86,
    sp: {
      y: -40,
      scale: 1.03,
      opacity: 1
    }
  });

  // gsap-box-e 背景画像：SPでも表示・軽量パララックス
  document.querySelectorAll('.m-section.gsap-box-e').forEach((sectionElement) => {
    const mediaElement = sectionElement.querySelector('.this-media');

    if (!mediaElement) return;

    gsap.fromTo(mediaElement,
      {
        opacity: 0
      },
      {
        opacity: 1,
        ease: "none",
        scrollTrigger: {
          trigger: sectionElement,
          start: isSP() ? 'top 90%' : 'top 70%',
          end: isSP() ? 'top 45%' : 'top 20%',
          scrub: 1,
          invalidateOnRefresh: true
        }
      }
    );

    gsap.fromTo(mediaElement,
      {
        y: isSP() ? 28 : 60
      },
      {
        y: isSP() ? -28 : -60,
        ease: "none",
        force3D: false,
        scrollTrigger: {
          trigger: sectionElement,
          start: 'top bottom',
          end: 'bottom top',
          scrub: isSP() ? 0.7 : 1,
          invalidateOnRefresh: true
        }
      }
    );
  });
}

//=====================================
// Fixed Next Button
//=====================================
//=====================================
// Fixed Next Button
// 横スクロールpin中の #section-service も正しく判定
//=====================================
function initFixedNextButton() {
  const fixedNextBtn = document.querySelector('#fixed-next-btn');
  const fixedNextLink = fixedNextBtn ? fixedNextBtn.querySelector('a') : null;
  const nextSections = gsap.utils.toArray('.m-section').filter((section) => section.id);

  let currentSection = null;
  let nextSection = null;

  if (!fixedNextBtn || !fixedNextLink || !nextSections.length) {
    return {
      update: function() {}
    };
  }

  function getActiveSection() {
    // service横スクロールpin中は、通常のoffset判定だとズレるため優先判定
    const serviceTrigger = ScrollTrigger.getById('service-horizontal');

    if (serviceTrigger && serviceTrigger.isActive) {
      return document.querySelector('#section-service');
    }

    const viewportCenter = window.scrollY + window.innerHeight * 0.5;

    let active = null;

    for (const section of nextSections) {
      const top = section.offsetTop;
      const bottom = top + section.offsetHeight;

      if (viewportCenter >= top && viewportCenter < bottom) {
        active = section;
        break;
      }
    }

    return active;
  }

  function update() {
    const active = getActiveSection();

    if (!active) return;

    currentSection = active;

    const currentIndex = nextSections.indexOf(active);
    nextSection = nextSections[currentIndex + 1];

    if (nextSection && nextSection.id) {
      fixedNextLink.setAttribute('href', '#' + nextSection.id);
      fixedNextBtn.classList.add('is-show');
    } else {
      fixedNextLink.setAttribute('href', '#');
      fixedNextBtn.classList.remove('is-show');
    }
  }

  const updateRaf = rafThrottle(update);

  window.addEventListener('scroll', updateRaf, {
    passive: true
  });

  window.addEventListener('resize', debounce(function() {
    update();

    if (isPC()) {
      ScrollTrigger.refresh();
    }
  }, 300));

  fixedNextLink.addEventListener('click', function(e) {
    e.preventDefault();

    update();

    if (!currentSection || !nextSection) return;

    const targetHash = '#' + nextSection.id;
    const chars = currentSection.querySelectorAll('.char');
    const sp = isSP();

    function restoreChars() {
      if (!chars.length) return;

      gsap.to(chars, {
        x: 0,
        y: 0,
        scale: 1,
        opacity: 1,
        rotation: 0,
        duration: sp ? 0.24 : 0.38,
        ease: "power3.out",
        stagger: {
          amount: sp ? 0.1 : 0.18,
          from: "center"
        }
      });
    }

    function moveToNext() {
      gsap.to(window, {
        scrollTo: {
          y: targetHash,
          autoKill: false
        },
        duration: sp ? 0.85 : 1.25,
        ease: "power3.out",
        onComplete: function() {
          update();
          restoreChars();

          // URLにhashを残さない
          history.replaceState(null, '', window.location.pathname + window.location.search);
        }
      });
    }

    if (chars.length) {
      gsap.killTweensOf(chars);

      gsap.to(chars, {
        x: function() {
          return gsap.utils.random(sp ? -120 : -480, sp ? 120 : 480);
        },
        y: function() {
          return gsap.utils.random(sp ? -120 : -480, sp ? 120 : 480);
        },
        scale: function() {
          return gsap.utils.random(sp ? 0.75 : 0.3, sp ? 1.35 : 2.0);
        },
        opacity: 0,
        rotation: function() {
          return gsap.utils.random(sp ? -60 : -360, sp ? 60 : 360);
        },
        duration: sp ? 0.3 : 0.45,
        ease: "power2.in",
        stagger: {
          amount: sp ? 0.12 : 0.24,
          from: "center"
        }
      });

      gsap.delayedCall(sp ? 0.36 : 0.55, moveToNext);
    } else {
      moveToNext();
    }
  });

  update();

  return {
    update: update
  };
}
//=====================================
// Common Scroll Animation
// .am-x-r / .am-x-l / .am-y-t / .am-y-b / .am-s-0 / .am-y-plx
//=====================================
function initCommonScrollAnimations() {
  const commonST = {
    start: 'top 85%',
    end: 'top 45%',
    scrub: 1,
    invalidateOnRefresh: false
  };

  document.querySelectorAll('.am-x-r').forEach((elm) => {
    gsap.from(elm, {
      x: isSP() ? 80 : 300,
      autoAlpha: 0,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        ...commonST
      }
    });
  });

  document.querySelectorAll('.am-x-l').forEach((elm) => {
    gsap.from(elm, {
      x: isSP() ? -80 : -300,
      autoAlpha: 0,
      opacity: 0,
      duration: 0.5,
      ease: "power2.out",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        ...commonST
      }
    });
  });

  document.querySelectorAll('.am-y-t').forEach((elm) => {
    gsap.from(elm, {
      y: isSP() ? -50 : -100,
      autoAlpha: 0,
      duration: 0.8,
      opacity: 1,
      ease: "power2.out",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        ...commonST
      }
    });
  });

  document.querySelectorAll('.am-y-b').forEach((elm) => {
    gsap.from(elm, {
      y: isSP() ? 50 : 100,
      autoAlpha: 0,
      duration: 0.8,
      opacity: 1,
      ease: "power2.out",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        ...commonST
      }
    });
  });

  document.querySelectorAll('.am-s-0').forEach((elm) => {
    gsap.from(elm, {
      scaleX: 0.5,
      duration: 0.8,
      opacity: 0,
      ease: "power2.out",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        ...commonST
      }
    });
  });

  document.querySelectorAll('.am-y-plx').forEach((elm) => {
    gsap.to(elm, {
      y: isSP() ? -40 : -100,
      ease: "none",
      force3D: false,
      scrollTrigger: {
        trigger: elm,
        start: 'top bottom',
        end: 'bottom top',
        scrub: 1,
        invalidateOnRefresh: false
      }
    });
  });
}

//=====================================
// Text Line Animation
//=====================================
function initTextLineAnimations() {
  document.querySelectorAll('.g-text-line.g-bt, .g-text-line.g-lt').forEach((elm) => {
    const paragraphs = elm.querySelectorAll('p');

    if (!paragraphs.length) return;

    gsap.fromTo(paragraphs,
      {
        y: isSP() ? 60 : 100,
        opacity: 0
      },
      {
        y: 0,
        opacity: 1,
        duration: isSP() ? 0.75 : 1.2,
        ease: 'expo.out',
        stagger: {
          each: isSP() ? 0.08 : 0.16
        },
        scrollTrigger: {
          trigger: elm,
          start: 'top 78%',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: false
        }
      }
    );
  });
}

//=====================================
// List Animation
//=====================================
function initListAnimations() {
  document.querySelectorAll('.g-text-line.g-list-v').forEach((elm) => {
    const items = elm.querySelectorAll(':scope > li');

    if (!items.length) return;

    items.forEach((item) => {
      const title = item.querySelector('h3');
      const text = item.querySelector('p');
      const num = item.querySelector('h3 i');

      gsap.set(item, {
        '--bg-scale-y': 0
      });

      gsap.set([title, text, num], {
        opacity: 0,
        force3D: false
      });

      gsap.set(title, {
        x: isSP() ? -18 : -36
      });

      gsap.set(text, {
        y: isSP() ? 20 : 32
      });

      gsap.set(num, {
        x: isSP() ? 24 : 48
      });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: item,
          start: 'top 78%',
          toggleActions: 'play none none reverse',
          invalidateOnRefresh: false
        }
      });

      tl.to(item, {
        '--bg-scale-y': 1,
        duration: 0.45,
        ease: 'power3.inOut'
      });

      if (title) {
        tl.to(title, {
          opacity: 1,
          x: 0,
          duration: 0.5,
          ease: 'power3.out'
        }, '-=0.25');
      }

      if (text) {
        tl.to(text, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          ease: 'power3.out'
        }, '-=0.42');
      }

      if (num) {
        tl.to(num, {
          opacity: 1,
          x: 0,
          duration: 0.42,
          ease: 'power3.out'
        }, '-=0.38');
      }
    });
  });
}

//=====================================
// Gallery Grid Motion
// DOM再構築なし。CSS Gridのまま列ごとに上下移動。
//=====================================
function initGalleryColumnMotion() {
  const gallery = document.querySelector('#section-gallery .photo-gallery');
  if (!gallery) return;

  const items = gsap.utils.toArray('#section-gallery .photo-gallery-item');
  if (!items.length) return;

  const mm = gsap.matchMedia();

  // PC: 8列
  mm.add('(min-width: 768px)', function() {
    items.forEach((item, i) => {
      const col = i % 8;
      const dir = col % 2 === 0 ? -1 : 1;

      gsap.set(item, {
        y: dir * 24,
        force3D: false
      });

      gsap.to(item, {
        y: dir * 90,
        ease: 'none',
        force3D: false,
        scrollTrigger: {
          id: 'gallery-item-pc-' + i,
          trigger: '#section-gallery',
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.2,
          invalidateOnRefresh: false
        }
      });
    });
  });

  // SP: 4列
  mm.add('(max-width: 767px)', function() {
    items.forEach((item, i) => {
      const col = i % 4;
      const dir = col % 2 === 0 ? -1 : 1;

     gsap.set(item, {
  y: dir * 20,
  force3D: false
});

gsap.to(item, {
  y: dir * 80,
  ease: 'none',
  force3D: false,
  scrollTrigger: {
    id: 'gallery-item-sp-' + i,
    trigger: '#section-gallery',
    start: 'top bottom',
    end: 'bottom top',
    scrub: 0.8,
    invalidateOnRefresh: false
  }
});
    });
  });
}

//=====================================
// Optional Legacy Animation
//=====================================
function initLegacyAnimations() {
  if (document.querySelector('#an-tit')) {
    gsap.from("#an-tit", {
      y: -80,
      opacity: 0,
      delay: 1.2,
      duration: 0.8,
      ease: "power2.out"
    });
  }

  if (document.querySelector('#an-stit')) {
    gsap.from("#an-stit", {
      x: -400,
      y: -400,
      rotation: -120,
      opacity: 0,
      delay: 1.5,
      duration: 0.8,
      scale: 4,
      ease: "power2.out",
      force3D: false
    });
  }

  if (document.querySelector('#char2 .char')) {
    gsap.from("#char2 .char", {
      y: -200,
      x: -200,
      scale: 0,
      rotation: 120,
      stagger: 0.04,
      opacity: 0,
      delay: 1.8,
      duration: 0.45,
      ease: "power2.out",
      force3D: false
    });
  }
}

//=====================================
// Refresh
//=====================================
function initFinalRefresh(updateFixedNextButton, fixedAnime) {
  window.addEventListener('load', function() {
    requestAnimationFrame(function() {
      if (isPC()) {
        ScrollTrigger.refresh();
      } else {
        // SPでCSS初期opacity:0のまま残る事故を防止
        document.querySelectorAll('.m-section .this-media').forEach((media) => {
          if (getComputedStyle(media).opacity === '0') {
            gsap.set(media, { opacity: 1 });
          }
        });
      }

      if (typeof updateFixedNextButton === 'function') {
        updateFixedNextButton();
      }

      if (typeof fixedAnime === 'function') {
        fixedAnime();
      }
    });
  }, {
    once: true
  });

  window.addEventListener('resize', debounce(function() {
    if (isPC()) {
      ScrollTrigger.refresh();
    }
  }, 300));
}

//=====================================
// Main
//=====================================
$(function() {
  setAutoScrollBehavior();

  initLoader();

  initSplitText();

  const fixedAnime = initHeaderScrollState();

  initHashControl();

  initHorizontalServiceScroll();

  initTitleAnimations();

  initGalleryTitle();

  initBackgroundParallax();

  const fixedNext = initFixedNextButton();

  initSmoothAnchorScroll();

  initCommonScrollAnimations();

  initTextLineAnimations();

  initListAnimations();

  initGalleryColumnMotion();

  initLegacyAnimations();

  initFinalRefresh(fixedNext.update, fixedAnime);



});
